import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  GetApp as ExportIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  AccountBalance as BalanceIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

// Redux Actions
import { getInvoices } from '../redux/actions/invoiceActions';
import { getCustomers } from '../redux/actions/customerActions';
import { getChitPayments } from '../redux/actions/chitActions';
import { getRepairOrderPayments } from '../redux/actions/repairOrderActions';
import { fetchProfitAndLoss } from '../redux/actions/reportActions';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Reports = () => {
  const dispatch = useDispatch();
  const { invoices, loading } = useSelector(state => state.invoices);
  const { customers } = useSelector(state => state.customers);
  const { chitPayments } = useSelector(state => state.chits);
  const { repairOrderPayments: repairOrderPaymentsState } = useSelector(state => state.repairOrders);
  const { pl, loading: reportLoading } = useSelector(state => state.reports);
  
  // Extract the payments array from the repair order payments state
  const repairOrderPayments = repairOrderPaymentsState?.payments || [];

  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [plBasis, setPlBasis] = useState('accrual');

  // Effects
  useEffect(() => {
    console.log('=== REPORTS PAGE LOADING ===');
    console.log('Dispatching actions to load data...');
    
    dispatch(getInvoices({ status: 'all', limit: 1000 })); // Include all invoices with high limit
    dispatch(getCustomers());
    dispatch(getChitPayments());
    dispatch(getRepairOrderPayments());
    // Preload P&L with current date filters if set
    const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
    const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
    dispatch(fetchProfitAndLoss('accrual', { startDate: startDateStr, endDate: endDateStr }));
    
    console.log('All actions dispatched');
    console.log('========================');
  }, [dispatch]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    dispatch(getInvoices({ status: 'all', limit: 1000 })); // Include all invoices with high limit
    dispatch(getChitPayments());
    dispatch(getRepairOrderPayments());
    const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
    const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
    dispatch(fetchProfitAndLoss(plBasis, { startDate: startDateStr, endDate: endDateStr }));
  };

  // Filter functions
  const renderProfitAndLoss = () => {
    const summary = pl?.summary || {};
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small">
            <InputLabel>Basis</InputLabel>
            <Select
              label="Basis"
              value={plBasis}
              onChange={(e) => {
                setPlBasis(e.target.value);
                // dispatch(fetchProfitAndLoss(e.target.value, { startDate, endDate }));
              }}
            >
              <MenuItem value="accrual">Accrual</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={() => {
            const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
            const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;
            dispatch(fetchProfitAndLoss(plBasis, { startDate: startDateStr, endDate: endDateStr }));
          }}>Run</Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p:2 }}>
              <Typography variant="subtitle2">Revenue / Cash In</Typography>
              <Typography variant="h5">₹{safeFormatCurrency(summary.revenue || summary.cashIn || 0)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p:2 }}>
              <Typography variant="subtitle2">Expenses / Cash Out</Typography>
              <Typography variant="h5">₹{safeFormatCurrency(summary.expenses || summary.cashOut || 0)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p:2 }}>
              <Typography variant="subtitle2">Net</Typography>
              <Typography variant="h5">₹{safeFormatCurrency(summary.netProfit || summary.netCash || 0)}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };
  const getFilteredInvoices = () => {
    let filtered = invoices || [];

    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.invoice_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }

    if (customerFilter) {
      filtered = filtered.filter(invoice => invoice.customer_id.toString() === customerFilter);
    }

    if (startDate) {
      filtered = filtered.filter(invoice => 
        new Date(invoice.invoice_date) >= startDate
      );
    }

    if (endDate) {
      filtered = filtered.filter(invoice => 
        new Date(invoice.invoice_date) <= endDate
      );
    }

    return filtered;
  };

  const getCancelledInvoices = () => {
    // Get all invoices without status filter, then filter for cancelled
    let filtered = invoices || [];

    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.invoice_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(invoice => invoice.customer_id.toString() === customerFilter);
    }

    if (startDate) {
      filtered = filtered.filter(invoice => 
        new Date(invoice.invoice_date) >= startDate
      );
    }

    if (endDate) {
      filtered = filtered.filter(invoice => 
        new Date(invoice.invoice_date) <= endDate
      );
    }

    // Always filter for cancelled invoices regardless of statusFilter
    return filtered.filter(invoice => invoice.status === 'cancelled');
  };

  const getCustomerBalances = () => {
    const balances = {};
    // Use all invoices without status filter for balance calculation
    let filteredInvoices = invoices || [];

    // Apply non-status filters for balance calculation
    if (searchTerm) {
      filteredInvoices = filteredInvoices.filter(invoice =>
        invoice.invoice_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (customerFilter) {
      filteredInvoices = filteredInvoices.filter(invoice => 
        invoice.customer_id.toString() === customerFilter
      );
    }

    if (startDate) {
      filteredInvoices = filteredInvoices.filter(invoice => 
        new Date(invoice.invoice_date) >= startDate
      );
    }

    if (endDate) {
      filteredInvoices = filteredInvoices.filter(invoice => 
        new Date(invoice.invoice_date) <= endDate
      );
    }

    filteredInvoices.forEach(invoice => {
      if (invoice.customer_id && invoice.status !== 'cancelled') {
        const customerId = invoice.customer_id;
        const customerName = invoice.customer?.name || 'Unknown Customer';
        
        if (!balances[customerId]) {
          balances[customerId] = {
            customerId,
            customerName,
            totalInvoices: 0,
            totalAmount: 0,
            totalPaid: 0,
            balance: 0
          };
        }

        balances[customerId].totalInvoices += 1;
        balances[customerId].totalAmount += parseFloat(invoice.grand_total || 0);
        
        // Calculate total paid from ACTIVE payments only
        const totalPaid = (invoice.payments || [])
          .filter(p => p.status === 'active')
          .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
        balances[customerId].totalPaid += totalPaid;
      }
    });

    // Calculate balance for each customer
    Object.keys(balances).forEach(customerId => {
      balances[customerId].balance = 
        balances[customerId].totalAmount - balances[customerId].totalPaid;
    });

    return Object.values(balances).filter(balance => balance.balance > 0);
  };

  const getCustomerPaymentHistory = () => {
    if (!selectedCustomer) return [];
    
    const customerId = selectedCustomer.id;
    const filteredInvoices = getFilteredInvoices().filter(
      invoice => invoice.customer_id === customerId
    );

    const paymentHistory = [];
    
    filteredInvoices.forEach(invoice => {
      if (invoice.payments && invoice.payments.length > 0) {
        invoice.payments.forEach(payment => {
          paymentHistory.push({
            ...payment,
            invoice_no: invoice.invoice_no,
            invoice_date: invoice.invoice_date,
            invoice_total: invoice.grand_total,
            customer_name: invoice.customer?.name,
            // Normalize payment date for display
            display_payment_date: payment.payment_date || (payment.created_at ? new Date(payment.created_at).toISOString().slice(0,10) : '')
          });
        });
      }
    });

    return paymentHistory.sort((a, b) => new Date(b.display_payment_date || b.payment_date || b.created_at) - new Date(a.display_payment_date || a.payment_date || a.created_at));
  };

  const getFilteredChitPayments = () => {
    let filtered = Array.isArray(chitPayments) ? chitPayments : [];

    if (searchTerm) {
      filtered = filtered.filter(payment => {
        const cc = payment.customerChit || payment.customer_chit;
        const customerName = cc?.customer?.name?.toLowerCase() || '';
        const groupName = cc?.chitGroup?.group_name?.toLowerCase() || cc?.chit_group?.name?.toLowerCase() || '';
        const ref = payment.reference_number?.toLowerCase() || '';
        const term = searchTerm.toLowerCase();
        return customerName.includes(term) || groupName.includes(term) || ref.includes(term);
      });
    }

    if (customerFilter) {
      filtered = filtered.filter(payment => {
        const cc = payment.customerChit || payment.customer_chit;
        return cc?.customer_id?.toString() === customerFilter;
      });
    }

    if (startDate) {
      filtered = filtered.filter(payment =>
        new Date(payment.payment_date) >= startDate
      );
    }

    if (endDate) {
      filtered = filtered.filter(payment =>
        new Date(payment.payment_date) <= endDate
      );
    }

    return filtered.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
  };

  const getFilteredRepairOrderPayments = () => {
    let filtered = Array.isArray(repairOrderPayments) ? repairOrderPayments : [];

    if (searchTerm) {
      filtered = filtered.filter(payment => {
        const ro = payment.repairOrder || payment.repair_order;
        const orderNumber = ro?.order_number?.toLowerCase() || '';
        const customerName = ro?.customer?.name?.toLowerCase() || '';
        const ref = payment.reference_number?.toLowerCase() || '';
        const term = searchTerm.toLowerCase();
        return orderNumber.includes(term) || customerName.includes(term) || ref.includes(term);
      });
    }

    if (customerFilter) {
      filtered = filtered.filter(payment => {
        const ro = payment.repairOrder || payment.repair_order;
        return ro?.customer_id?.toString() === customerFilter;
      });
    }

    if (startDate) {
      filtered = filtered.filter(payment =>
        new Date(payment.payment_date) >= startDate
      );
    }

    if (endDate) {
      filtered = filtered.filter(payment =>
        new Date(payment.payment_date) <= endDate
      );
    }

    return filtered.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
  };

  // Utility functions
  const safeFormatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'warning';
      case 'pending': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  // Export functions
  const exportCancelledInvoices = () => {
    const cancelledInvoices = getCancelledInvoices();
    const csvContent = [
      ['Invoice No', 'Customer', 'Date', 'Amount', 'Status', 'Cancel Remarks'],
      ...cancelledInvoices.map(invoice => [
        invoice.invoice_no,
        invoice.customer?.name || 'N/A',
        invoice.invoice_date,
        invoice.grand_total,
        invoice.status,
        invoice.cancel_remarks || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cancelled_invoices_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportCustomerBalances = () => {
    const balances = getCustomerBalances();
    const csvContent = [
      ['Customer', 'Total Invoices', 'Total Amount', 'Total Paid', 'Balance'],
      ...balances.map(balance => [
        balance.customerName,
        balance.totalInvoices,
        balance.totalAmount.toFixed(2),
        balance.totalPaid.toFixed(2),
        balance.balance.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer_balances_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportPaymentHistory = () => {
    const paymentHistory = getCustomerPaymentHistory();
    const csvContent = [
      ['Customer', 'Invoice No', 'Invoice Date', 'Invoice Total', 'Payment Date', 'Amount', 'Mode', 'Reference'],
      ...paymentHistory.map(payment => [
        payment.customer_name,
        payment.invoice_no,
        payment.invoice_date,
        payment.invoice_total,
        payment.payment_date,
        payment.amount,
        payment.payment_mode_code,
        payment.reference_number || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment_history_${selectedCustomer?.name}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportChitPayments = () => {
    const chitPayments = getFilteredChitPayments();
    const csvContent = [
      ['Customer', 'Chit Group', 'Payment Date', 'Amount', 'Payment Mode', 'Reference Number', 'Notes'],
      ...chitPayments.map(payment => [
        payment.customer_chit?.customer?.name || 'N/A',
        payment.customer_chit?.chit_group?.name || 'N/A',
        payment.payment_date,
        payment.amount,
        payment.payment_mode_code || 'N/A',
        payment.reference_number || 'N/A',
        payment.notes || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chit_payments_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportRepairOrderPayments = () => {
    const repairOrderPayments = getFilteredRepairOrderPayments();
    const csvContent = [
      ['Customer', 'Order Number', 'Payment Date', 'Amount', 'Payment Mode', 'Reference Number', 'Notes'],
      ...repairOrderPayments.map(payment => [
        payment.repair_order?.customer?.name || 'N/A',
        payment.repair_order?.order_number || 'N/A',
        payment.payment_date,
        payment.amount,
        payment.payment_mode_code || 'N/A',
        payment.reference_number || 'N/A',
        payment.notes || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `repair_order_payments_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Render functions
  const renderCancelledInvoices = () => {
    const cancelledInvoices = getCancelledInvoices();
    const paginatedInvoices = cancelledInvoices.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    // Debug: Log invoice data
    console.log('=== CANCELLED INVOICES DEBUG ===');
    console.log('Total invoices loaded:', invoices?.length || 0);
    console.log('Cancelled invoices found:', cancelledInvoices.length);
    console.log('Sample invoice statuses:', invoices?.slice(0, 5).map(inv => ({ id: inv.id, status: inv.status, invoice_no: inv.invoice_no })));
    console.log('All invoice statuses:', [...new Set(invoices?.map(inv => inv.status) || [])]);
    console.log('================================');

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Cancelled Invoices ({cancelledInvoices.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportCancelledInvoices}
            disabled={cancelledInvoices.length === 0}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice No</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Cancel Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoice_no}</TableCell>
                  <TableCell>{invoice.customer?.name || 'N/A'}</TableCell>
                  <TableCell>{invoice.invoice_date}</TableCell>
                  <TableCell>₹{safeFormatCurrency(invoice.grand_total)}</TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.status?.toUpperCase() || 'UNKNOWN'}
                      color={getStatusColor(invoice.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{invoice.cancel_remarks || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={cancelledInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    );
  };

  const renderCustomerBalances = () => {
    const balances = getCustomerBalances();

    // Debug: Log customer balance data
    console.log('=== CUSTOMER BALANCES DEBUG ===');
    console.log('Total invoices loaded:', invoices?.length || 0);
    console.log('Filtered invoices:', getFilteredInvoices().length);
    console.log('Customer balances found:', balances.length);
    console.log('Sample invoices with payments:', invoices?.slice(0, 3).map(inv => ({
      id: inv.id,
      invoice_no: inv.invoice_no,
      customer_id: inv.customer_id,
      customer_name: inv.customer?.name,
      status: inv.status,
      grand_total: inv.grand_total,
      payments_count: inv.payments?.length || 0,
      payments_total: inv.payments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0
    })));
    console.log('All customers with invoices:', [...new Set(invoices?.map(inv => inv.customer_id).filter(Boolean) || [])]);
    console.log('================================');

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Customer Balances ({balances.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportCustomerBalances}
            disabled={balances.length === 0}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Total Invoices</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Total Paid</TableCell>
                <TableCell>Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {balances.map((balance) => (
                <TableRow key={balance.customerId}>
                  <TableCell>{balance.customerName}</TableCell>
                  <TableCell>{balance.totalInvoices}</TableCell>
                  <TableCell>₹{safeFormatCurrency(balance.totalAmount)}</TableCell>
                  <TableCell>₹{safeFormatCurrency(balance.totalPaid)}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      color={balance.balance > 0 ? 'error.main' : 'success.main'}
                      fontWeight="bold"
                    >
                      ₹{safeFormatCurrency(balance.balance)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderPaymentHistory = () => {
    const paymentHistory = getCustomerPaymentHistory();
    const paginated = Array.isArray(paymentHistory) ? paymentHistory.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    ) : [];

    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            fullWidth
            options={customers || []}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCustomer}
            onChange={(event, newValue) => setSelectedCustomer(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Customer"
                variant="outlined"
                placeholder="Search customers..."
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.phone} {option.email && `• ${option.email}`}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>

        {selectedCustomer && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Payment History - {selectedCustomer.name} ({paymentHistory.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportPaymentHistory}
                disabled={paymentHistory.length === 0}
              >
                Export CSV
              </Button>
            </Box>

              <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice No</TableCell>
                    <TableCell>Invoice Date</TableCell>
                    <TableCell>Invoice Total</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((payment, index) => (
                    <TableRow key={`${payment.invoice_id}-${payment.id || index}`}>
                      <TableCell>{payment.invoice_no}</TableCell>
                      <TableCell>{payment.invoice_date}</TableCell>
                      <TableCell>₹{safeFormatCurrency(payment.invoice_total)}</TableCell>
                      <TableCell>{payment.display_payment_date || payment.payment_date || (payment.created_at ? new Date(payment.created_at).toISOString().slice(0,10) : '')}</TableCell>
                      <TableCell>₹{safeFormatCurrency(payment.amount)}</TableCell>
                      <TableCell>{payment.payment_mode_code}</TableCell>
                      <TableCell>{payment.reference_number || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={Array.isArray(paymentHistory) ? paymentHistory.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
          </Box>
        )}

        {!selectedCustomer && (
          <Alert severity="info">
            Please select a customer to view payment history
          </Alert>
        )}
      </Box>
    );
  };

  const renderChitPayments = () => {
    const chitPayments = getFilteredChitPayments();
    const paginatedPayments = Array.isArray(chitPayments) ? chitPayments.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    ) : [];

    // Debug: Log chit payment data
    console.log('=== CHIT PAYMENTS DEBUG ===');
    console.log('Chit payments from Redux state:', chitPayments);
    console.log('Chit payments type:', typeof chitPayments);
    console.log('Is array:', Array.isArray(chitPayments));
    console.log('Filtered chit payments:', chitPayments?.length || 0);
    console.log('Sample chit payment:', chitPayments?.[0]);
    // State shape debug (no hooks in helper): log available keys from outer state
    // Note: avoid using hooks inside non-hook functions
    console.log('============================');

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Customer Chits Payments ({Array.isArray(chitPayments) ? chitPayments.length : 0})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportChitPayments}
            disabled={!Array.isArray(chitPayments) || chitPayments.length === 0}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Chit Group</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayments.map((payment) => {
                const cc = payment.customerChit || payment.customer_chit;
                const customerName = cc?.customer?.name || 'N/A';
                const groupName = cc?.chitGroup?.group_name || cc?.chit_group?.name || 'N/A';
                // Payment mode might be in details if MULTIPLE
                const firstDetail = Array.isArray(payment.paymentDetails || payment.payment_details)
                  ? (payment.paymentDetails || payment.payment_details)[0]
                  : null;
                const paymentModeLabel = firstDetail?.paymentMode?.code
                  || firstDetail?.payment_mode?.code
                  || payment.payment_mode_code
                  || payment.payment_method
                  || 'N/A';
                return (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {customerName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {groupName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.payment_date}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        ₹{safeFormatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={paymentModeLabel}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {payment.reference_number || firstDetail?.reference_number || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {payment.notes || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={Array.isArray(chitPayments) ? chitPayments.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    );
  };

  const renderRepairOrderPayments = () => {
    const repairOrderPayments = getFilteredRepairOrderPayments();
    const paginatedPayments = Array.isArray(repairOrderPayments) ? repairOrderPayments.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    ) : [];

    // Debug: Log repair order payment data
    console.log('=== REPAIR ORDER PAYMENTS DEBUG ===');
    console.log('Repair order payments from Redux state:', repairOrderPayments);
    console.log('Repair order payments type:', typeof repairOrderPayments);
    console.log('Is array:', Array.isArray(repairOrderPayments));
    console.log('Filtered repair order payments:', repairOrderPayments?.length || 0);
    console.log('Sample repair order payment:', repairOrderPayments?.[0]);
    console.log('Full repair order payments state:', repairOrderPaymentsState);
    // State shape debug (no hooks in helper): log state outside if needed
    console.log('===================================');

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Repair Orders Payments ({Array.isArray(repairOrderPayments) ? repairOrderPayments.length : 0})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportRepairOrderPayments}
            disabled={!Array.isArray(repairOrderPayments) || repairOrderPayments.length === 0}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Order Number</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayments.map((payment) => {
                const ro = payment.repairOrder || payment.repair_order;
                const customerName = ro?.customer?.name || 'N/A';
                const orderNumber = ro?.order_number || 'N/A';
                const paymentMode = payment.paymentMode?.code || payment.payment_mode_code || 'N/A';
                return (
                <TableRow key={payment.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {customerName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {orderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.payment_date}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      ₹{safeFormatCurrency(payment.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={paymentMode}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.reference_number || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {payment.notes || 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={Array.isArray(repairOrderPayments) ? repairOrderPayments.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" fontWeight="bold">
                Reports & Analytics
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customers || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={customers?.find(customer => customer.id.toString() === customerFilter) || null}
                    onChange={(event, newValue) => {
                      setCustomerFilter(newValue ? newValue.id.toString() : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        variant="outlined"
                        placeholder="Search customers..."
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab
                  icon={<CancelIcon />}
                  label="Cancelled Invoices"
                  iconPosition="start"
                />
                <Tab
                  icon={<BalanceIcon />}
                  label="Customer Balances"
                  iconPosition="start"
                />
                <Tab
                  icon={<PaymentIcon />}
                  label="Payment History"
                  iconPosition="start"
                />
                <Tab
                  icon={<ReceiptIcon />}
                  label="Chit Payments"
                  iconPosition="start"
                />
                <Tab
                  icon={<ReceiptIcon />}
                  label="Repair Payments"
                  iconPosition="start"
                />
                
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {renderCancelledInvoices()}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {renderCustomerBalances()}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {renderPaymentHistory()}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              {renderChitPayments()}
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              {renderRepairOrderPayments()}
            </TabPanel>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;